import type { DesignGroup } from '../types/storeReviewOrderStep.types';
import { STORE_BULK_PRICING_TYPE } from '../constants/storeCheckout.constants';
import type { StoreReviewPricingModel } from './storeReviewPricing.utils';
import { mapStoreReviewPricingModelToBulkPricingType } from './storeCheckoutBulkPricingTypeUpdate.utils';
import {
  getStoreReviewExactBreakpointUnitPrice,
  getStoreReviewDesignGroupQuantityTiers,
  getStoreReviewQuotedItemPrice,
} from './storeReviewPricing.utils';
import { resolveStoreReviewProductInfo } from './storeReviewProductInfo.utils';
import type {
  BulkPricingType,
  IStorePricingBreakpoint,
  IStorePricingOrderItem,
  IUpdateStoreRequest,
} from '@/common/interfaces/store.interface';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';

export interface IBuildStorePricingUpdateParams {
  orderId: number;
  group: DesignGroup;
  minimumQuantity: number;
  pricingModel: StoreReviewPricingModel;
  designGroupMoq?: number;
  donationAmounts?: Record<string, string>;
  groupIndex?: number;
  storeType?: 'group-order' | 'brand-drop' | 'fundraiser' | 'permanent-store' | '';
}

const roundPrice = (value: number): number => Number(value.toFixed(2));

const parseAmountOrZero = (amount: string | null): number => {
  if (!amount) {
    return 0;
  }

  const parsed = Number.parseFloat(amount);

  return Number.isNaN(parsed) ? 0 : parsed;
};

const buildStorePricingBreakpoint = (
  quantity: number,
  pricePerItem: number,
  breakpointNumber: number,
  donationAmount: string | null,
  markupAmount: string | null
): IStorePricingBreakpoint => {
  const roundedPrice = roundPrice(pricePerItem);
  const additiveAmount = parseAmountOrZero(donationAmount) + parseAmountOrZero(markupAmount);
  const finalPricePerItem = roundPrice(roundedPrice + additiveAmount);

  return {
    quantity,
    price_per_item: roundedPrice,
    donation_amount: donationAmount,
    markup_amount: markupAmount,
    breakpoint_number: breakpointNumber,
    final_price_per_item: finalPricePerItem,
    gpm_percentage: null,
    is_quoted_by_quoter: true,
    quoter_price: roundedPrice,
  };
};

const resolveItemDonationAmounts = (
  donationAmount: string | undefined,
  storeType: IBuildStorePricingUpdateParams['storeType']
): { donationAmount: string | null; markupAmount: string | null } => {
  const trimmedAmount = donationAmount?.trim() ?? '';
  const parsedAmount = Number.parseFloat(trimmedAmount);
  const formattedAmount =
    trimmedAmount && !Number.isNaN(parsedAmount) && parsedAmount > 0
      ? parsedAmount.toFixed(2)
      : null;

  if (storeType === 'fundraiser') {
    return { donationAmount: formattedAmount, markupAmount: null };
  }

  if (storeType === 'brand-drop') {
    return { donationAmount: null, markupAmount: formattedAmount };
  }

  return { donationAmount: null, markupAmount: null };
};

export const resolveStorePricingBreakpointQuantities = (
  group: DesignGroup,
  minimumQuantity: number,
  pricingModel: StoreReviewPricingModel,
  designGroupMoq?: number
): number[] => {
  if (pricingModel === 'same') {
    return [minimumQuantity];
  }

  const quantities = getStoreReviewDesignGroupQuantityTiers(group, designGroupMoq);

  return quantities.filter((quantity) => quantity >= minimumQuantity);
};

export const buildStorePricingOrderItem = (
  item: ICartItemDetail,
  minimumQuantity: number,
  breakpointQuantities: number[],
  pricingType: BulkPricingType,
  donationAmount?: string,
  storeType?: IBuildStorePricingUpdateParams['storeType']
): IStorePricingOrderItem => {
  const { donationAmount: resolvedDonationAmount, markupAmount } = resolveItemDonationAmounts(
    donationAmount,
    storeType
  );

  const breakpoints = breakpointQuantities
    .map((quantity, index) => {
      // Prefer exact quoter tier prices. For single-tier (price stays same), allow item.price fallback.
      const unitPrice =
        getStoreReviewExactBreakpointUnitPrice(quantity, item.priceList) ??
        (breakpointQuantities.length === 1 ? getStoreReviewQuotedItemPrice(quantity, item) : null);

      if (unitPrice == null || unitPrice <= 0) {
        return null;
      }

      return buildStorePricingBreakpoint(
        quantity,
        unitPrice,
        index,
        resolvedDonationAmount,
        markupAmount
      );
    })
    .filter((breakpoint): breakpoint is IStorePricingBreakpoint => breakpoint != null);

  // #region agent log
  {
    const priceListSnapshot = (item.priceList ?? []).map((bp) => ({
      quantity: Number(bp.quantity),
      finalPricePerUnit: bp.finalPricePerUnit,
    }));
    const resolvedTiers = breakpointQuantities.map((quantity) => {
      const exactMatch = priceListSnapshot.find((bp) => bp.quantity === quantity);
      const unitPrice = getStoreReviewExactBreakpointUnitPrice(quantity, item.priceList);
      return {
        quantity,
        unitPrice,
        hasExactPriceListMatch: exactMatch != null,
        exactPrice: exactMatch?.finalPricePerUnit ?? null,
      };
    });
    const uniquePrices = [...new Set(breakpoints.map((bp) => bp.final_price_per_item))];
    fetch('http://127.0.0.1:7533/ingest/039f2598-1ae5-494b-9857-3e928ecbad58', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Debug-Session-Id': 'bbce4b',
      },
      body: JSON.stringify({
        sessionId: 'bbce4b',
        runId: 'post-fix',
        hypothesisId: 'H-A,H-E',
        location: 'storeCheckoutPricingUpdate.utils.ts:buildStorePricingOrderItem',
        message: 'Building store pricing breakpoints for save',
        data: {
          orderItemId: item.id,
          pricingType,
          minimumQuantity,
          priceListLength: priceListSnapshot.length,
          priceListSnapshot,
          breakpointQuantities,
          resolvedTiers,
          savedBreakpoints: breakpoints.map((bp) => ({
            quantity: bp.quantity,
            final_price_per_item: bp.final_price_per_item,
          })),
          allSavedPricesIdentical: uniquePrices.length <= 1 && breakpoints.length > 1,
          uniqueSavedPriceCount: uniquePrices.length,
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
  }
  // #endregion

  return {
    order_item_id: item.id,
    pricing_type: pricingType,
    minimum_quantity: minimumQuantity,
    breakpoints,
  };
};

export const buildStorePricingUpdatePayload = ({
  orderId,
  group,
  minimumQuantity,
  pricingModel,
  designGroupMoq,
  donationAmounts = {},
  groupIndex = 0,
  storeType = '',
}: IBuildStorePricingUpdateParams): IUpdateStoreRequest => {
  const pricingType = mapStoreReviewPricingModelToBulkPricingType(pricingModel);
  const breakpointQuantities = resolveStorePricingBreakpointQuantities(
    group,
    minimumQuantity,
    pricingModel,
    designGroupMoq
  );

  return {
    order_id: orderId,
    order_items: group.items.map((item) => ({
      id: item.id,
      product_description: resolveStoreReviewProductInfo(item),
    })),
    pricing: {
      design_groups: [
        {
          items: group.items.map((item, itemIndex) => {
            const donationKey = `donation-${groupIndex}-${itemIndex}`;

            return buildStorePricingOrderItem(
              item,
              minimumQuantity,
              breakpointQuantities,
              pricingType,
              donationAmounts[donationKey],
              storeType
            );
          }),
        },
      ],
    },
  };
};

/** @deprecated Use buildStorePricingUpdatePayload */
export const buildStorePriceDropsPricingUpdatePayload = (
  params: Omit<IBuildStorePricingUpdateParams, 'pricingModel'>
): IUpdateStoreRequest => buildStorePricingUpdatePayload({ ...params, pricingModel: 'drops' });

/** @deprecated Use buildStorePricingOrderItem */
export const buildStorePriceDropsPricingOrderItem = (
  item: ICartItemDetail,
  minimumQuantity: number,
  breakpointQuantities: number[],
  donationAmount?: string,
  storeType?: IBuildStorePricingUpdateParams['storeType']
): IStorePricingOrderItem =>
  buildStorePricingOrderItem(
    item,
    minimumQuantity,
    breakpointQuantities,
    STORE_BULK_PRICING_TYPE.PRICE_DROPS,
    donationAmount,
    storeType
  );

export const buildStorePricingSaveKey = (
  group: DesignGroup,
  minimumQuantity: number,
  pricingModel: StoreReviewPricingModel
): string =>
  `${pricingModel}:${minimumQuantity}:${group.items
    .map(
      (item) =>
        `${item.id}:${item.minQuant ?? 0}:${item.price ?? 0}:${
          item.priceList
            ?.map((breakpoint) => `${breakpoint.quantity}:${breakpoint.finalPricePerUnit}`)
            .join(',') ?? ''
        }`
    )
    .join('|')}`;

/** @deprecated Use buildStorePricingSaveKey */
export const buildStorePriceDropsPricingSaveKey = (
  group: DesignGroup,
  minimumQuantity: number
): string => buildStorePricingSaveKey(group, minimumQuantity, 'drops');
